<!--
  The trailer half of the board's "Set truck/trailer" dialog (spec 46 §1.6,
  plan §4.13). It replaces a drop-down that only gave unit numbers, so a
  dispatcher had to pick a trailer to learn it was in the shop or already on
  another load.

  One table, modelled on AssigneePicker.vue: every unit of one kind (`kind`,
  a trailer unless told otherwise) with its derived state for THIS load's
  commitment, read from GET /equipment/availability?kind=&load_id=. The
  server judges each unit the way the assignment check will (the load itself
  excluded), so the row and the save agree: available, on another load, in
  the shop (a hold or a work order in progress), blocked (an expired
  credential, an uncertified defect), inactive. Each row says why, and where
  the unit was last seen ("Near North Yard · 12 min ago").

  The buttons mirror the server's rules:
    - no reason, or only advisory ones (a trailer on another load is
      reported, never refused, §1.5): Select picks it at once, the warning
      still shown on the row;
    - a reason marked `overridable: false` (a hold, a truck already on a
      load): no button, "Can't assign";
    - any other reason: "Select…" opens a step for a typed reason, for a
      viewer with scheduling rights; without them, "Needs scheduling rights".

  This component only decides. It emits `pick` with { id, reason } (id null
  for "No trailer") and the dialog keeps its own Save, PATCH, 409 path and
  AvailabilityNotice. A failed read lists nothing and says so; the server
  still checks when the dialog saves. Every button here is secondary: a pick
  commits nothing, and the dialog's Save is the view's one primary.
-->
<template>
  <div class="lw-p-equipment-picker">
    <template v-if="pending">
      <p class="lw-p-equipment-picker__step-title">
        Use <strong>{{ pending.unit_number }}</strong> anyway?
      </p>
      <LwNotice tone="warning" :title="pending.label">
        <ul class="lw-p-equipment-picker__list">
          <li v-for="(line, i) in reasonLines(pending)" :key="i">{{ line }}</li>
        </ul>
      </LwNotice>
      <LwField
        v-model="reason"
        type="textarea"
        :rows="3"
        :maxlength="2000"
        label="Reason to assign anyway"
        description="Saved with the load when you press Save."
        data-test="equipment-reason"
      />
      <div class="lw-p-equipment-picker__step-actions">
        <LwButton variant="secondary" @click="back">Back to the list</LwButton>
        <LwButton
          variant="secondary"
          :disabled="!reason.trim()"
          data-test="equipment-select-with-reason"
          @click="pickPending"
        >
          Select with reason
        </LwButton>
      </div>
    </template>

    <template v-else>
      <p v-if="failed" class="lw-p-equipment-picker__hint" data-test="equipment-hint">
        {{ kindWord }}s could not be checked, so none are listed. Close and open the dialog to try
        again; the server still checks when you save.
      </p>
      <div class="lw-p-equipment-picker__bar">
        <LwField
          v-model="query"
          class="lw-p-equipment-picker__search"
          :label="`Find a ${kindNoun}`"
          type="search"
        />
        <LwButton
          v-if="selectedId != null"
          size="sm"
          variant="secondary"
          data-test="equipment-clear"
          @click="clear"
        >
          No {{ kindNoun }}
        </LwButton>
      </div>
      <LwTable
        :columns="columns"
        :rows="visibleRows"
        row-key="key"
        density="compact"
        :loading="loading"
        :error="null"
        :caption="`${kindWord}s`"
        :empty-title="emptyTitle"
        :empty-body="emptyBody"
      >
        <template #cell-unit="{ row }">
          <span class="lw-p-equipment-picker__name">{{ row.unit_number }}</span>
          <span class="lw-p-equipment-picker__kind">{{ unitKindLabel(row) }}</span>
        </template>
        <template #cell-availability="{ row }">
          <span class="lw-p-equipment-picker__availability">
            <LwChip axis="feedback" :value="unitStateTone(row.state)" :label="row.label" />
            <span
              v-for="(line, i) in reasonLines(row)"
              :key="i"
              class="lw-p-equipment-picker__reason"
              >{{ line }}</span
            >
            <span
              v-if="locationLine(row.location)"
              class="lw-p-equipment-picker__reason"
              :class="{ 'lw-p-equipment-picker__reason--stale': locationLine(row.location).stale }"
              data-test="equipment-location"
              >{{ locationLine(row.location).text }}</span
            >
          </span>
        </template>
        <template #cell-action="{ row }">
          <span
            v-if="row.selected"
            class="lw-p-equipment-picker__current"
            :data-test="`equipment-selected-${row.key}`"
            >{{ row.current ? 'Assigned' : 'Selected' }}</span
          >
          <LwButton
            v-else-if="actionFor(row) === 'pick'"
            size="sm"
            variant="secondary"
            :aria-label="`Select ${row.unit_number}`"
            :data-test="`equipment-select-${row.key}`"
            @click="select(row)"
          >
            Select
          </LwButton>
          <LwButton
            v-else-if="actionFor(row) === 'override'"
            size="sm"
            variant="secondary"
            :aria-label="`Select ${row.unit_number}, with a reason`"
            :data-test="`equipment-select-${row.key}`"
            @click="select(row)"
          >
            Select…
          </LwButton>
          <span
            v-else
            class="lw-p-equipment-picker__unavailable"
            :data-test="`equipment-none-${row.key}`"
            >{{ unavailableWord(row) }}</span
          >
        </template>
      </LwTable>
    </template>
  </div>
</template>

<script>
import LwButton from '../../components/lw/LwButton.vue';
import LwChip from '../../components/lw/LwChip.vue';
import LwField from '../../components/lw/LwField.vue';
import LwNotice from '../../components/lw/LwNotice.vue';
import LwTable from '../../components/lw/LwTable.vue';
import {
  hasHardBlock,
  locationLine,
  reasonLabel,
  unitKindLabel,
  unitStateTone,
} from '../Scheduling/schedulingFormat';

// Free first, then the ones worth a look, then the ones that need a reason
// or cannot be used at all.
const STATE_ORDER = {
  available: 0,
  needs_review: 1,
  on_load: 1,
  blocked: 2,
  in_shop: 3,
  inactive: 4,
};

export default {
  name: 'EquipmentPicker',
  components: { LwButton, LwChip, LwField, LwNotice, LwTable },
  props: {
    // 'trailer' or 'truck'.
    kind: {
      type: String,
      default: 'trailer',
      validator: value => ['trailer', 'truck'].includes(value),
    },
    // The board row being edited (its id is what the read is judged for).
    load: {
      type: Object,
      required: true,
    },
    // scheduling.manage: may type a reason to use a unit over a warning.
    canOverride: {
      type: Boolean,
      default: false,
    },
    // The dialog's current choice (null for none).
    selectedId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ['pick'],
  data() {
    return {
      body: null,
      loading: true,
      failed: false,
      query: '',
      pending: null,
      reason: '',
    };
  },
  computed: {
    kindNoun() {
      return this.kind === 'truck' ? 'truck' : 'trailer';
    },
    kindWord() {
      return this.kind === 'truck' ? 'Truck' : 'Trailer';
    },
    columns() {
      return [
        { key: 'unit', label: 'Unit' },
        { key: 'availability', label: 'Availability' },
        { key: 'action', label: 'Assign', width: '7rem' },
      ];
    },
    rows() {
      const units = (this.body && this.body.units) || [];
      const current = this.body && this.body.current != null ? String(this.body.current) : null;
      const selected = this.selectedId != null ? String(this.selectedId) : null;
      return units
        .map(u => ({
          ...u,
          key: String(u.id),
          current: current !== null && String(u.id) === current,
          selected: selected !== null && String(u.id) === selected,
        }))
        .sort(
          (a, b) =>
            Number(b.current) - Number(a.current) ||
            (STATE_ORDER[a.state] ?? 5) - (STATE_ORDER[b.state] ?? 5) ||
            String(a.unit_number).localeCompare(String(b.unit_number), undefined, {
              numeric: true,
            })
        );
    },
    visibleRows() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter(r => r.current || String(r.unit_number).toLowerCase().includes(q));
    },
    emptyTitle() {
      if (this.failed) return `No ${this.kindNoun}s listed`;
      if (this.query.trim()) return `No ${this.kindNoun} matches`;
      return `No ${this.kindNoun}s yet`;
    },
    emptyBody() {
      if (this.failed) return 'The list could not be read.';
      if (this.query.trim()) return `Clear the search to see every ${this.kindNoun}.`;
      return this.kind === 'truck'
        ? 'Add trucks on the Trucks page.'
        : 'Add a unit of type Trailer on the Trucks page.';
    },
  },
  watch: {
    load() {
      this.pending = null;
      this.reason = '';
      this.fetchUnits();
    },
  },
  created() {
    this.fetchUnits();
  },
  methods: {
    locationLine,
    unitKindLabel,
    unitStateTone,
    fetchUnits() {
      this.loading = true;
      this.failed = false;
      const url = `/equipment/availability?kind=${encodeURIComponent(
        this.kind
      )}&load_id=${encodeURIComponent(this.load.id)}`;
      return new Promise(resolve => resolve(this.$http.get(url)))
        .then(res => {
          const body = res && res.data;
          this.body = body && Array.isArray(body.units) ? body : null;
          this.failed = !this.body;
        })
        .catch(() => {
          this.body = null;
          this.failed = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    blocking(row) {
      return (row.reasons || []).filter(r => r && !r.advisory);
    },
    // 'pick' (Select now), 'override' (Select… opens the reason step), or
    // null (no button).
    actionFor(row) {
      if (hasHardBlock(row.reasons)) return null;
      // An inactive unit is only listed because the load already has it;
      // keeping it is allowed, choosing it again after a change is not.
      if (row.state === 'inactive' && !row.current) return null;
      if (!this.blocking(row).length) return 'pick';
      return this.canOverride ? 'override' : null;
    },
    unavailableWord(row) {
      if (hasHardBlock(row.reasons) || row.state === 'inactive') return 'Can’t assign';
      return 'Needs scheduling rights';
    },
    reasonLines(row) {
      return (row.reasons || []).map(r => r.message || reasonLabel(r.code));
    },
    select(row) {
      const action = this.actionFor(row);
      if (action === 'override') {
        this.pending = row;
        this.reason = '';
        return;
      }
      if (action === 'pick') this.$emit('pick', { id: String(row.id), reason: null });
    },
    pickPending() {
      const reason = this.reason.trim();
      if (!this.pending || !reason) return;
      this.$emit('pick', { id: String(this.pending.id), reason });
      this.back();
    },
    clear() {
      this.$emit('pick', { id: null, reason: null });
    },
    back() {
      this.pending = null;
      this.reason = '';
    },
  },
};
</script>

<style scoped lang="scss">
.lw-p-equipment-picker {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-3);
}

.lw-p-equipment-picker__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--lw-space-3);
}

.lw-p-equipment-picker__search {
  flex: 1 1 12rem;
}

.lw-p-equipment-picker__hint {
  margin: 0;
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-secondary);
}

.lw-p-equipment-picker__name {
  display: block;
  font: var(--lw-type-body);
  font-weight: 600;
}

.lw-p-equipment-picker__kind {
  display: block;
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
}

.lw-p-equipment-picker__availability {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--lw-space-1);
}

.lw-p-equipment-picker__reason {
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
}

// An old position is still the last thing known; it is shown, quieter.
.lw-p-equipment-picker__reason--stale {
  color: var(--lw-fg-tertiary);
}

.lw-p-equipment-picker__current,
.lw-p-equipment-picker__unavailable {
  font: var(--lw-type-caption-strong);
  color: var(--lw-fg-secondary);
}

.lw-p-equipment-picker__list {
  margin: 0;
  padding-left: var(--lw-space-4);
}

.lw-p-equipment-picker__step-title {
  margin: 0;
  font: var(--lw-type-body);
}

.lw-p-equipment-picker__step-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--lw-space-3);
}
</style>
